import { inputVariant } from "@babackapp/ui/components/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { cn } from "@babackapp/ui/lib/utils";
import { Cross1Icon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { forwardRef, useState } from "react";
import FormInputContainer from "./form-input-container";

type FormTagsInput = {
  className?: string;
  canFocus?: boolean;
  label: string;
  description?: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  disabled?: boolean | string;
  /**
   * Warnings by tag value, e.g. a value that matches nothing on the shop: the
   * tag is flagged and the message shown in its tooltip.
   */
  tagWarnings?: ReadonlyMap<string, string>;
};

const FormTagsInput = forwardRef(
  (
    {
      className,
      canFocus = true,
      label,
      description,
      value = [],
      onChange,
      onBlur,
      disabled,
      tagWarnings,
    }: FormTagsInput,
    ref,
  ) => {
    void ref;
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const removeTag = (index: number) => {
      onBlur?.();
      const nextItems = [...value];
      nextItems.splice(index, 1);
      onChange(nextItems);
    };

    const addItem = () => {
      const trimedValue = inputValue.trim();
      if (!trimedValue) return;
      onBlur?.();
      const next = [...value, trimedValue];
      onChange(next);
      setInputValue("");
    };

    const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        return addItem();
      }

      if (e.key === "Backspace" && inputValue === "") {
        const lastIndex = value.length - 1;
        return removeTag(lastIndex);
      }
    };

    return (
      <FormInputContainer
        label={label}
        description={description}
        disabled={disabled}
      >
        <div
          className={cn(
            inputVariant,
            "group flex flex-wrap h-auto max-h-auto gap-1",
            className,
            canFocus &&
              focused &&
              "ring-2 ring-offset-background ring-offset-2 ring-ring",
            disabled && "cursor-not-allowed",
          )}
        >
          <TagsList
            value={value}
            onRemove={removeTag}
            disabled={!!disabled}
            tagWarnings={tagWarnings}
          />
          <input
            className="focus:ring-0 focus:border-none focus:outline-none outline-none border-none flex-1"
            onKeyDown={onKeyPress}
            value={inputValue}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={!!disabled}
          />
        </div>
      </FormInputContainer>
    );
  },
);

type TagsListProps = {
  value: string[];
  onRemove: (index: number) => void;
  disabled?: boolean;
  tagWarnings?: ReadonlyMap<string, string>;
};

function TagsList({ value, onRemove, disabled, tagWarnings }: TagsListProps) {
  return (
    <>
      {value?.map((value, index) => {
        const warning = tagWarnings?.get(value);
        const tag = (
          <button
            key={index}
            role="button"
            className={cn(
              "bg-background rounded-lg flex items-center gap-2 py-1 px-2 border border-border",
              warning && "border-amber-500 text-amber-700",
            )}
            disabled={disabled}
            aria-description={warning}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(index);
            }}
          >
            {warning && (
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
            )}
            {value}
            <Cross1Icon className="w-4 h-4" />
          </button>
        );
        if (!warning) return tag;
        return (
          <Tooltip key={index}>
            <TooltipTrigger asChild>{tag}</TooltipTrigger>
            <TooltipContent className="max-w-xs">{warning}</TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}

FormTagsInput.displayName = "FormTagsInput";

export default FormTagsInput;
